import { InputAdornment, OutlinedInput } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";

import { Box } from "ui/box/box";
import { InputControl } from "ui/forms/input-control/input-control";
import { mergeSx } from "ui/merge-sx";
import {
  AFFIX_CLASS,
  CONTROL_SX,
  NumberFieldStepper,
  StepperButton,
} from "ui/number-field/number-field.styles";
import { type NumberFieldProps } from "ui/number-field/number-field.types";
import { useNumberField } from "ui/number-field/use-number-field";

const INCREASE_LABEL = "Increase";
const DECREASE_LABEL = "Decrease";

export const NumberField = ({
  ref,
  id,
  name,
  value,
  defaultValue,
  onValueChange,
  min,
  max,
  step,
  label,
  required,
  disabled,
  readOnly,
  errMessage,
  helperText,
  infoTooltip,
  prefix,
  suffix,
  size = "medium",
  placeholder,
  onBlur,
  onKeyDown,
  "data-testid": dataTestId,
  slotProps,
  sx,
  controlSx,
  ...rest
}: NumberFieldProps) => {
  const {
    fieldId,
    text,
    showSuffix,
    canIncrement,
    canDecrement,
    increment,
    decrement,
    handleChange,
    handleBlur,
    handleKeyDown,
    handleStepperMouseDown,
    spinbuttonProps,
    describedBy,
    testId,
  } = useNumberField({
    id,
    name,
    value,
    defaultValue,
    onValueChange,
    min,
    max,
    step,
    disabled,
    readOnly,
    errMessage,
    helperText,
    suffix,
    onBlur,
    onKeyDown,
    "data-testid": dataTestId,
  });

  return (
    <InputControl
      id={fieldId}
      label={label}
      infoTooltip={infoTooltip}
      required={required}
      disabled={disabled}
      errorMessage={errMessage}
      helperText={helperText}
      size={size}
      sx={sx}>
      <OutlinedInput
        // Ahead of `rest` so a call site that computes its own description still out-ranks it.
        aria-describedby={describedBy}
        {...rest}
        type="text"
        value={text}
        inputRef={ref}
        id={fieldId}
        name={name}
        placeholder={placeholder ?? ""}
        disabled={disabled}
        readOnly={readOnly}
        error={!!errMessage}
        fullWidth
        size={size}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        sx={mergeSx(CONTROL_SX, controlSx)}
        startAdornment={
          prefix && (
            <InputAdornment position="start">
              <Box component="span" className={AFFIX_CLASS}>
                {prefix}
              </Box>
            </InputAdornment>
          )
        }
        endAdornment={
          <>
            {showSuffix && (
              <InputAdornment position="end">
                <Box component="span" className={AFFIX_CLASS}>
                  {suffix}
                </Box>
              </InputAdornment>
            )}
            {/* Out of the tab order: a spinbutton is stepped with the arrow keys in the input. */}
            <NumberFieldStepper stepperSize={size}>
              <StepperButton
                aria-label={INCREASE_LABEL}
                tabIndex={-1}
                disabled={!canIncrement}
                onMouseDown={handleStepperMouseDown}
                onClick={increment}>
                <ArrowDropUpIcon />
              </StepperButton>
              <StepperButton
                aria-label={DECREASE_LABEL}
                tabIndex={-1}
                disabled={!canDecrement}
                onMouseDown={handleStepperMouseDown}
                onClick={decrement}>
                <ArrowDropDownIcon />
              </StepperButton>
            </NumberFieldStepper>
          </>
        }
        slotProps={{
          ...slotProps,
          input: { ...spinbuttonProps, "data-testid": testId, ...slotProps?.input },
        }}
      />
    </InputControl>
  );
};
